import {useIntl} from 'react-intl'
import {Button, InputTextFormik, SelectFormik} from '../../../../../_gori/partials/widgets'
import {CHATBOT_MODE, MessageItemCommonProps, MessageOrderInfo} from '../../definitions'
import {
  DIMENSIONS,
  DIMENSIONS_PLACEHOLDER,
  OPTION_DIMENSION_UNIT,
  OPTION_WEIGHT_UNIT,
} from '../../../../../_gori/constants'
import {useMemo} from 'react'
import clsx from 'clsx'
import {useChatbotContext} from '../../context'
import {MessageItemWrapper} from './MessageItemWrapper'
import {getCSSVariableValue} from '../../../../../_gori/assets/ts/_utils'
import {darken} from '@mui/material'
import {AutocompleteAddress} from './AutocompleteAddress'
import {useOrderInfo} from '../../hooks'

type MessageItemOrderInfoProps = MessageItemCommonProps<MessageOrderInfo>

export const MessageItemOrderInfo = ({message}: MessageItemOrderInfoProps) => {
  const intl = useIntl()
  const {disabledUserAction, mode} = useChatbotContext()

  const {disabled, formik, addressError, setAddressError} = useOrderInfo({message})

  const isDefaultMode = useMemo(() => mode === CHATBOT_MODE.DEFAULT, [mode])

  const color = getCSSVariableValue('--bs-white')

  const customSelectInputStyle = {
    styleInput: {
      background: 'transparent',
      height: 26,
      minHeight: 26,
      backgroundImage: 'none',
      borderColor: 'transparent',
      '&:hover': {
        background: darken(getCSSVariableValue('--bs-primary-200'), 0.2),
      },
      color,
      textAlign: 'right',
      padding: 0,
      border: '1px solid white',
      borderRadius: '30px',
      paddingLeft: '4px',
      paddingRight: '4px',
    },
    styleDropdownIndicator: {
      color,
      padding: 0,
      marginRight: 0,
      '&:hover': {
        color,
      },
    },
  }
  return (
    <div
      className={clsx(
        {'cursor-not-allowed opacity-75': disabledUserAction},
        'd-flex flex-column flex-fill'
      )}
    >
      <MessageItemWrapper
        message={message}
        wrapperClassName={clsx({
          'mw-75 align-self-end': !isDefaultMode,
          'w-100': isDefaultMode,
        })}
        className='bg-primary-200'
      >
        <div className='mb-6 d-flex flex-fill'>
          <AutocompleteAddress
            formik={formik}
            prefix='from'
            className='w-100'
            disabled={disabled}
            label={`${intl.formatMessage({
              id: 'SHIP_FROM_ADDRESS',
            })}:`}
            required={false}
            labelClassName='text-white fw-bold fs-6 pt-4 w-250px'
            placeholder={intl.formatMessage({id: 'ORDER_INFO_ADDRESS_PLACEHOLDER'})}
            addressError={addressError.from}
            setAddressError={(errorMessage: string) => setAddressError('from', errorMessage)}
          />
        </div>
        <div className='mb-6 d-flex flex-fill'>
          <AutocompleteAddress
            formik={formik}
            prefix='to'
            className='w-100'
            disabled={disabled}
            label={`${intl.formatMessage({
              id: 'SHIP_TO_ADDRESS',
            })}:`}
            required={false}
            labelClassName='text-white fw-bold fs-6 pt-4 w-250px'
            placeholder={intl.formatMessage({id: 'ORDER_INFO_ADDRESS_PLACEHOLDER'})}
            addressError={addressError.to}
            setAddressError={(errorMessage: string) => setAddressError('to', errorMessage)}
          />
        </div>
        <div className='d-flex justify-content-between align-items-center mb-3'>
          <div className='fs-6 fw-bold text-white'>
            {intl.formatMessage({id: 'DIMENSIONS'})} <span className='fw-light'>{'L*W*H'}</span>
          </div>
          <SelectFormik
            className={'dimension-unit-select overflow-hidden'}
            placeholder=''
            label={''}
            labelClassName={'text-muted'}
            options={OPTION_DIMENSION_UNIT}
            name={'dimension.0.dimension_unit'}
            formik={formik}
            emptyDefault={false}
            hideIndicatorSeparator
            isSearchable={false}
            disabled={disabled}
            {...customSelectInputStyle}
            styleSingleValue={{
              color: disabled ? 'white' : 'defaultColor',
            }}
          />
        </div>
        <div className='d-flex gap-4 mt-2'>
          {DIMENSIONS.map((item, index) => {
            return (
              <div className='d-flex flex-fill w-100' key={index}>
                <InputTextFormik
                  type='number'
                  min={0}
                  formik={formik}
                  name={`dimension.0.${item}`}
                  configAppend={{
                    name: <div className='px-1'>{DIMENSIONS_PLACEHOLDER[item]}</div>,
                    position: 'right',
                    classAppend: 'right-3',
                  }}
                  disabled={disabled}
                  className={clsx('flex-fill')}
                />
              </div>
            )
          })}
        </div>
        <div className='d-flex justify-content-between align-items-end' style={{gap: 50}}>
          <div
            className={clsx({
              'mw-50': isDefaultMode,
              'd-flex flex-fill flex-column': !isDefaultMode,
            })}
          >
            <div className='d-flex justify-content-between align-items-center mt-7 mb-3'>
              <div className='fs-5 fw-bold ps-2 text-white'>
                {intl.formatMessage({id: 'WEIGHT'})}
              </div>
              <SelectFormik
                label={''}
                placeholder=''
                options={OPTION_WEIGHT_UNIT}
                name={'dimension.0.weight_unit'}
                formik={formik}
                emptyDefault={false}
                hideIndicatorSeparator
                isSearchable={false}
                disabled={disabled}
                {...customSelectInputStyle}
                styleSingleValue={{
                  color: disabled ? 'white' : 'defaultColor',
                }}
              />
            </div>

            <InputTextFormik
              type='number'
              min={0}
              formik={formik}
              name={'dimension.0.weight'}
              className='mt-1'
              disabled={disabled}
            />
          </div>
          {!isDefaultMode && <div className='d-flex flex-fill' />}
          <div className='d-flex flex-fill justify-content-end'>
            <Button
              className={clsx('btn btn-advanced-secondary btn-sm')}
              label={intl.formatMessage({id: 'SUBMIT'})}
              disabled={disabled}
              event={formik.handleSubmit}
            ></Button>
          </div>
        </div>
      </MessageItemWrapper>
    </div>
  )
}
